import React from 'react';
import { ChatWindow } from '../organisms';
import { ChatMessage as ChatMessageType, Conversation } from '../../types';
// META2-04 PR #352: Ley 172-13 consent gate
import { useAiConsent, AiConsentModal } from '@/features/ai-consent';

interface ChatbotTemplateProps {
  messages: ChatMessageType[];
  isLoading?: boolean;
  isOpen: boolean;
  minimized: boolean;
  onSendMessage: (message: string) => void;
  onMinimize: () => void;
  onClose: () => void;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  title?: string;
  // Historial
  conversations?: Conversation[];
  isHistoryOpen?: boolean;
  onShowHistory?: () => void;
  onCloseHistory?: () => void;
  onSelectConversation?: (conversationId: string) => void;
  onNewConversation?: () => void;
  onRenameConversation?: (conversationId: string, newName: string) => void;
  onDeleteConversation?: (conversationId: string) => void;
  selectedConversationId?: string | null;
}

const ChatbotTemplate: React.FC<ChatbotTemplateProps> = ({
  messages,
  isLoading = false,
  isOpen,
  minimized,
  onSendMessage,
  onMinimize,
  onClose,
  position = 'bottom-right',
  title,
  conversations = [],
  isHistoryOpen = false,
  onShowHistory,
  onCloseHistory,
  onSelectConversation,
  onNewConversation,
  onRenameConversation,
  onDeleteConversation,
  selectedConversationId,
}) => {
  // META2-04 PR #352: consent gate — modal abierto si user no tiene consent
  // activo y el chatbot está abierto. Si rechaza, cerrar el chatbot.
  // GH#1875: `enabled: isOpen` — este template se monta en cada página aunque el
  // chatbot esté cerrado; sin el gate, /ai/consent salía en cada carga.
  const { hasConsent, isLoading: consentLoading } = useAiConsent({ enabled: isOpen });

  if (!isOpen) {
    return null;
  }

  // Bloquea el render del chat mientras consent loading o no granted
  if (!consentLoading && !hasConsent) {
    return (
      <AiConsentModal
        open={true}
        onAccept={() => {
          // Re-render automático via hook state; chat usable inmediato
        }}
        onReject={onClose}
      />
    );
  }

  const getPositionClasses = () => {
    switch (position) {
      case 'bottom-right':
        return 'bottom-24 right-6';
      case 'bottom-left':
        return 'bottom-24 left-6';
      case 'top-right':
        return 'top-24 right-6';
      case 'top-left':
        return 'top-24 left-6';
      default:
        return 'bottom-24 right-6';
    }
  };

  const getSizeClasses = () => {
    if (minimized) {
      return 'w-80 h-16';
    }
    return 'w-96 h-[600px]';
  };

  return (
    <div
      className={`fixed ${getPositionClasses()} ${getSizeClasses()} z-40 transition-all duration-300`}
    >
      {minimized ? (
        <div className="h-full bg-primary-600 rounded-lg shadow-lg flex items-center justify-between px-4">
          <span className="text-white font-semibold text-sm">
            Asistente Virtual
          </span>
          <button
            onClick={onMinimize}
            className="text-white hover:bg-primary-700 rounded p-1"
            aria-label="Expandir"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            </svg>
          </button>
        </div>
      ) : (
        <ChatWindow
          messages={messages}
          isLoading={isLoading}
          onSendMessage={onSendMessage}
          onMinimize={onMinimize}
          onClose={onClose}
          title={title}
          conversations={conversations}
          isHistoryOpen={isHistoryOpen}
          onShowHistory={onShowHistory}
          onCloseHistory={onCloseHistory}
          onSelectConversation={onSelectConversation}
          onNewConversation={onNewConversation}
          onRenameConversation={onRenameConversation}
          onDeleteConversation={onDeleteConversation}
          selectedConversationId={selectedConversationId}
        />
      )}
    </div>
  );
};

export default ChatbotTemplate;

